import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ColorPicker from './ColorPicker';
import { BrandingThemeOptions } from '../../../contexts/BrandingContext';
import { defaultBrandColors } from '../../../contexts/ThemeContext';

interface ThemeOptionProps {
  themeOptions: BrandingThemeOptions | undefined;
  onUpdateThemeOptions: (newOptions: BrandingThemeOptions) => void;
}

interface ColorConfig {
  key: string;
  label: string;
  description: string;
  path: string[];
  defaultValue: string;
}

const ThemeOptions: React.FC<ThemeOptionProps> = ({
  themeOptions,
  onUpdateThemeOptions,
}) => {
  const [expanded, setExpanded] = useState<string | false>('palette');

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  // Helper function to get a nested value from an object using a path array
  const getNestedValue = (obj: any, path: string[]): any => {
    return path.reduce(
      (prev, curr) =>
        prev && prev[curr] !== undefined ? prev[curr] : undefined,
      obj,
    );
  };

  // Helper function to update a nested value in an object using a path array
  const updateNestedValue = (obj: any, path: string[], value: any): any => {
    if (path.length === 0) return value;

    const current = { ...obj };
    let temp = current;

    for (let i = 0; i < path.length - 1; i++) {
      if (!temp[path[i]]) temp[path[i]] = {};
      temp[path[i]] = { ...temp[path[i]] };
      temp = temp[path[i]];
    }

    temp[path[path.length - 1]] = value;
    return current;
  };

  // Handle color change
  const handleColorChange = (color: string, path: string[]) => {
    if (!themeOptions) return;

    const updatedOptions = updateNestedValue(themeOptions, path, color);
    onUpdateThemeOptions(updatedOptions);
  };

  // Define all color options with their paths and descriptions
  const colorOptions: ColorConfig[] = [
    // Primary colors
    {
      key: 'primary-main',
      label: 'Primary Main',
      description:
        'The main primary color used for buttons, links, and active elements',
      path: ['palette', 'primary', 'main'],
      defaultValue: defaultBrandColors.primary.main,
    },
    {
      key: 'primary-light',
      label: 'Primary Light',
      description:
        'Lighter shade of the primary color, used for hover states and backgrounds',
      path: ['palette', 'primary', 'light'],
      defaultValue: defaultBrandColors.primary.light,
    },
    {
      key: 'primary-dark',
      label: 'Primary Dark',
      description: 'Darker shade of the primary color, used for active states',
      path: ['palette', 'primary', 'dark'],
      defaultValue: defaultBrandColors.primary.dark,
    },

    // Secondary colors
    {
      key: 'secondary-main',
      label: 'Secondary Main',
      description:
        'The main secondary color used for less prominent buttons and UI elements',
      path: ['palette', 'secondary', 'main'],
      defaultValue: defaultBrandColors.secondary.main,
    },
    {
      key: 'secondary-light',
      label: 'Secondary Light',
      description: 'Lighter shade of the secondary color',
      path: ['palette', 'secondary', 'light'],
      defaultValue: defaultBrandColors.secondary.light,
    },
    {
      key: 'secondary-dark',
      label: 'Secondary Dark',
      description: 'Darker shade of the secondary color',
      path: ['palette', 'secondary', 'dark'],
      defaultValue: defaultBrandColors.secondary.dark,
    },
    {
      key: 'secondary-medium',
      label: 'Secondary Medium',
      description:
        'Mid-tone of secondary color, used for specialized UI elements',
      path: ['palette', 'secondary', 'medium'],
      defaultValue: defaultBrandColors.secondary.medium || '',
    },

    // Error colors
    {
      key: 'error-main',
      label: 'Error Main',
      description:
        'Used for error messages, validation feedback, and error states. Also used for delete actions and permanent removal indicators.',
      path: ['palette', 'error', 'main'],
      defaultValue: defaultBrandColors.error.main,
    },
    {
      key: 'error-light',
      label: 'Error Light',
      description:
        'Lighter shade of error color for backgrounds and subtle indicators',
      path: ['palette', 'error', 'light'],
      defaultValue: defaultBrandColors.error.light,
    },
    {
      key: 'error-dark',
      label: 'Error Dark',
      description: 'Darker shade of error color for active states',
      path: ['palette', 'error', 'dark'],
      defaultValue: defaultBrandColors.error.dark,
    },

    // Success colors
    {
      key: 'success-main',
      label: 'Success Main',
      description:
        'Used for success messages, validation feedback, and completed states',
      path: ['palette', 'success', 'main'],
      defaultValue: defaultBrandColors.success.main,
    },
    {
      key: 'success-light',
      label: 'Success Light',
      description:
        'Lighter shade of success color for backgrounds and subtle indicators',
      path: ['palette', 'success', 'light'],
      defaultValue: defaultBrandColors.success.light,
    },
    {
      key: 'success-dark',
      label: 'Success Dark',
      description: 'Darker shade of success color for active states',
      path: ['palette', 'success', 'dark'],
      defaultValue: defaultBrandColors.success.dark,
    },

    // Warning colors
    {
      key: 'warning-main',
      label: 'Warning Main',
      description: 'Used for warning indicators',
      path: ['palette', 'warning', 'main'],
      defaultValue: defaultBrandColors.warning.main,
    },
    {
      key: 'warning-light',
      label: 'Warning Light',
      description: 'Light warning color used for warning message backgrounds',
      path: ['palette', 'warning', 'light'],
      defaultValue: defaultBrandColors.warning.light,
    },
    {
      key: 'warning-dark',
      label: 'Warning Dark',
      description: 'Dark warning color used for warning message text',
      path: ['palette', 'warning', 'dark'],
      defaultValue: defaultBrandColors.warning.dark,
    },

    // State colors
    {
      key: 'states-readonly',
      label: 'Readonly State',
      description: 'Background color for form fields in readonly state',
      path: ['palette', 'states', 'readonly'],
      defaultValue: defaultBrandColors.states.readonly,
    },
    {
      key: 'states-disabled',
      label: 'Disabled State',
      description: 'Background color for disabled form elements',
      path: ['palette', 'states', 'disabled'],
      defaultValue: defaultBrandColors.states.disabled,
    },
    {
      key: 'states-default',
      label: 'Default State',
      description: 'Default background color for form elements',
      path: ['palette', 'states', 'default'],
      defaultValue: defaultBrandColors.states.default,
    },
    {
      key: 'states-focus',
      label: 'Focus State',
      description: 'Border color for focused form elements',
      path: ['palette', 'states', 'focus'],
      defaultValue: defaultBrandColors.states.focus,
    },
  ];

  // Grey scale options
  const greyScaleOptions = [50, 100, 200, 300, 400, 500, 600, 700].map(
    (level) => ({
      key: `grey-${level}`,
      label: `Grey ${level}`,
      description:
        level < 400
          ? `Light grey tone, used for backgrounds and subtle UI elements`
          : level < 600
            ? `Medium grey tone, used for secondary text and borders`
            : `Dark grey tone, used for primary text and important UI elements`,
      path: ['palette', 'grey', level.toString()],
      defaultValue: (defaultBrandColors.grey as any)[level],
    }),
  );

  // Background colors
  const backgroundOptions = [
    {
      key: 'background-default',
      label: 'Background Default',
      description: 'Default background color for the application',
      path: ['palette', 'background', 'default'],
      defaultValue: '#ffffff',
    },
    {
      key: 'background-paper',
      label: 'Background Paper',
      description: 'Background color for paper elements like cards and dialogs',
      path: ['palette', 'background', 'paper'],
      defaultValue: '#ffffff',
    },
    {
      key: 'background-secondary',
      label: 'Background Secondary',
      description: 'Secondary background color for alternative sections',
      path: ['palette', 'background', 'secondary'],
      defaultValue: '#e8edf6',
    },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Theme Options
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        Customize all aspects of your theme by configuring the options below.
      </Typography>
      {/* Color Palette Section */}
      <Accordion
        expanded={expanded === 'palette'}
        onChange={handleAccordionChange('palette')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Color Palette</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Configure the primary colors used throughout the application.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Primary & Secondary Colors
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {colorOptions.slice(0, 7).map((option) => (
              <Grid
                key={option.key}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <ColorPicker
                  color={
                    getNestedValue(themeOptions, option.path) ||
                    option.defaultValue
                  }
                  onChange={(color) => handleColorChange(color, option.path)}
                  label={option.label}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {option.description}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Status & Feedback Colors
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {colorOptions.slice(7, 17).map((option) => (
              <Grid
                key={option.key}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <ColorPicker
                  color={
                    getNestedValue(themeOptions, option.path) ||
                    option.defaultValue
                  }
                  onChange={(color) => handleColorChange(color, option.path)}
                  label={option.label}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {option.description}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
            UI State Colors
          </Typography>
          <Grid container spacing={3}>
            {colorOptions.slice(17).map((option) => (
              <Grid
                key={option.key}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <ColorPicker
                  color={
                    getNestedValue(themeOptions, option.path) ||
                    option.defaultValue
                  }
                  onChange={(color) => handleColorChange(color, option.path)}
                  label={option.label}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {option.description}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* Grey Scale Section */}
      <Accordion
        expanded={expanded === 'greyscale'}
        onChange={handleAccordionChange('greyscale')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Grey Scale</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Configure grey tones used for text, borders, and UI elements.
          </Typography>

          <Grid container spacing={3}>
            {greyScaleOptions.map((option) => (
              <Grid
                key={option.key}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3,
                }}
              >
                <ColorPicker
                  color={
                    getNestedValue(themeOptions, option.path) ||
                    option.defaultValue
                  }
                  onChange={(color) => handleColorChange(color, option.path)}
                  label={option.label}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {option.description}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* Background Colors Section */}
      <Accordion
        expanded={expanded === 'backgrounds'}
        onChange={handleAccordionChange('backgrounds')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Background Colors</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Configure background colors for different parts of the application.
          </Typography>

          <Grid container spacing={3}>
            {backgroundOptions.map((option) => (
              <Grid
                key={option.key}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                }}
              >
                <ColorPicker
                  color={
                    getNestedValue(themeOptions, option.path) ||
                    option.defaultValue
                  }
                  onChange={(color) => handleColorChange(color, option.path)}
                  label={option.label}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {option.description}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* Typography Section */}
      <Accordion
        expanded={expanded === 'typography'}
        onChange={handleAccordionChange('typography')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Typography</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Configure font family and text settings.
          </Typography>

          <Grid container spacing={3}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Font Family"
                value={
                  getNestedValue(themeOptions, ['typography', 'fontFamily']) ||
                  '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                }
                onChange={(e) => {
                  const updatedOptions = updateNestedValue(
                    themeOptions || {},
                    ['typography', 'fontFamily'],
                    e.target.value,
                  );
                  onUpdateThemeOptions(updatedOptions);
                }}
                helperText="Font stack in order of preference (e.g. 'Inter, Roboto, Arial, sans-serif')"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* Shape Section */}
      <Accordion
        expanded={expanded === 'shape'}
        onChange={handleAccordionChange('shape')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Shape & Borders</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Configure border radius and other shape properties.
          </Typography>

          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                type="number"
                label="Border Radius"
                InputProps={{ inputProps: { min: 0 } }}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown')
                    e.preventDefault();
                }}
                value={
                  getNestedValue(themeOptions, ['shape', 'borderRadius']) || 4
                }
                onChange={(e) => {
                  const updatedOptions = updateNestedValue(
                    themeOptions || {},
                    ['shape', 'borderRadius'],
                    parseInt(e.target.value),
                  );
                  onUpdateThemeOptions(updatedOptions);
                }}
                helperText="Border radius in pixels for components (default: 4)"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* Spacing Section */}
      <Accordion
        expanded={expanded === 'spacing'}
        onChange={handleAccordionChange('spacing')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Spacing</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Configure the base spacing unit used throughout the application.
          </Typography>

          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                type="number"
                label="Base Spacing Unit"
                InputProps={{ inputProps: { min: 1 } }}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown')
                    e.preventDefault();
                }}
                value={getNestedValue(themeOptions, ['spacing']) || 8}
                onChange={(e) => {
                  const updatedOptions = updateNestedValue(
                    themeOptions || {},
                    ['spacing'],
                    parseInt(e.target.value),
                  );
                  onUpdateThemeOptions(updatedOptions);
                }}
                helperText="Base spacing unit in pixels (default: 8). Multipliers like 2x or 0.5x are applied to this base value."
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ThemeOptions;
